import React, { useEffect, useRef, useState } from 'react';

import type { QuickInferenceSegmentation } from '../../../helpers/api/quick-inference/quick-inference';
import { useCategoryLabels } from './useCategoryLabels';

interface Point {
  x: number;
  y: number;
}

// Points come from the backend as "x1\y1,x2\y2,..." (backslash-separated coords).
function parsePoints(raw: string): Point[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((pair) => {
      const [x, y] = pair.split('\\');
      return { x: parseFloat(x), y: parseFloat(y) };
    })
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}

interface Props {
  imageUrl: string;
  segmentations: QuickInferenceSegmentation[];
  showLabels?: boolean;
  /** When true, scales the canvas to fit the parent container height. */
  constrained?: boolean;
}

/**
 * Standalone canvas overlay for Quick Inference. Draws segmentation polygons /
 * ellipses on top of a base frame image. Intentionally self-contained (not
 * shared with the clinical viewer) so it can evolve freely.
 */
function SegmentationOverlay({
  imageUrl,
  segmentations,
  showLabels = true,
  constrained = false,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState(false);
  const { labelForCategoryId, colorForCategoryId } = useCategoryLabels();

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (cancelled) {
        return;
      }
      const w = img.naturalWidth || img.width || 1280;
      const h = img.naturalHeight || img.height || 720;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);

      ctx.save();
      ctx.lineWidth = Math.max(2, Math.round(w / 400));
      ctx.globalAlpha = 0.95;
      ctx.setLineDash([8, 6]);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      (segmentations || []).forEach((seg) => {
        const pts = parsePoints(seg?.points || '');
        if (!pts.length) {
          return;
        }
        const gType = String(seg?.type ?? '').trim().toUpperCase();
        const catId = Number(seg?.category_id);
        const color = Number.isFinite(catId) ? colorForCategoryId(catId) : 'red';

        ctx.strokeStyle = color;
        if (gType === 'ELLIPSE' || gType === 'CIRCLE') {
          const xs = pts.map((p) => p.x);
          const ys = pts.map((p) => p.y);
          const minX = Math.min(...xs);
          const maxX = Math.max(...xs);
          const minY = Math.min(...ys);
          const maxY = Math.max(...ys);
          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          const radiusX = Math.max(1, (maxX - minX) / 2);
          const radiusY = Math.max(1, (maxY - minY) / 2);
          ctx.beginPath();
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
          ctx.stroke();
        } else {
          ctx.beginPath();
          pts.forEach((pt, idx) => {
            if (idx === 0) {
              ctx.moveTo(pt.x, pt.y);
            } else {
              ctx.lineTo(pt.x, pt.y);
            }
          });
          ctx.stroke();
        }

        if (showLabels) {
          const anchor = pts[0];
          const text = Number.isFinite(catId) ? labelForCategoryId(catId) : '';
          if (text && anchor) {
            ctx.save();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
            ctx.font = `bold ${Math.max(
              12,
              Math.round(w / 80)
            )}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
            const padX = 6;
            const padY = 4;
            const metrics = ctx.measureText(text);
            const boxW = Math.ceil(metrics.width) + padX * 2;
            const fontSize = Math.max(12, Math.round(w / 80));
            const boxH = fontSize + padY * 2;
            const boxX = Math.max(0, anchor.x);
            const boxY = Math.max(0, anchor.y - boxH);
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            ctx.fillRect(boxX, boxY, boxW, boxH);
            ctx.fillStyle = color;
            ctx.textBaseline = 'top';
            ctx.fillText(text, boxX + padX, boxY + padY);
            ctx.restore();
          }
        }
      });

      ctx.restore();
    };
    img.onerror = () => {
      if (!cancelled) {
        setError(true);
      }
    };
    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl, segmentations, showLabels, labelForCategoryId, colorForCategoryId]);

  const canvasStyle: React.CSSProperties = constrained
    ? {
        maxHeight: '100%',
        maxWidth: '100%',
        width: 'auto',
        height: 'auto',
        display: 'block',
        borderRadius: 6,
      }
    : { width: '100%', height: 'auto', display: 'block', borderRadius: 6 };

  const canvas = (
    <canvas ref={canvasRef} style={canvasStyle} />
  );

  if (error) {
    return (
      <div className="flex h-40 items-center justify-center rounded bg-gray-100 text-xs text-gray-500">
        Frame unavailable
      </div>
    );
  }

  if (constrained) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        {canvas}
      </div>
    );
  }

  return canvas;
}

export default SegmentationOverlay;
